import React, { useState, useEffect, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody, Form, FormFeedback, Input, Label, Row, Col, Button } from 'reactstrap';
import { useFormik } from "formik";
import * as Yup from "yup";
import Flatpickr from "react-flatpickr";
import Select from 'react-select';

const eventTypeOptions = [
  { value: 'Rapor', label: 'Rapor' },
  { value: 'Bakım', label: 'Bakım' },
  { value: 'Onarım', label: 'Onarım' },
  { value: 'Teklif', label: 'Teklif' },
  { value: 'Hatırlatma', label: 'Hatırlatma' },
  { value: 'Proje Başlangıcı', label: 'Proje Başlangıcı' },
  { value: 'Proje Bitişi', label: 'Proje Bitişi' },
  { value: 'Fatura', label: 'Fatura' },
  { value: 'Tedarikçi', label: 'Tedarikçi' },
  { value: 'Teknik Servis', label: 'Teknik Servis' },
];

const CreateEventModal = ({ isOpen, toggle, selectedEvent, onEventAdded, pb }) => {
  const [relatedData, setRelatedData] = useState({
    customers: [],
    services: [],
    products: [],
    invoices: [],
  });
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchRelatedData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [customerRecords, serviceRecords, productRecords, invoiceRecords] = await Promise.all([
        pb.collection('customers').getFullList(),
        pb.collection('services').getFullList(),
        pb.collection('products').getFullList(),
        pb.collection('invoices').getFullList(),
      ]);

      setRelatedData({
        customers: customerRecords.map(customer => ({ value: customer.id, label: customer.name })),
        services: serviceRecords.map(service => ({ value: service.id, label: service.name || service.title || "İsimsiz Hizmet" })),
        products: productRecords.map(product => ({ value: product.id, label: product.product_name })),
        invoices: invoiceRecords.map(invoice => ({ value: invoice.id, label: invoice.title || invoice.name || "İsimsiz Teklif" })),
      });
    } catch (error) {
      console.error("Error fetching related data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [pb]);

  useEffect(() => {
    fetchRelatedData();
    if (selectedEvent) {
      setDateRange([selectedEvent.start, selectedEvent.end]);
    }
  }, [selectedEvent, fetchRelatedData]);

  const validationSchema = Yup.object({
    title: Yup.string().required("Etkinlik adı zorunludur"),
    event_type: Yup.array().min(1, "En az bir etkinlik tipi seçilmelidir"),
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      event_type: [],
      description: "",
      location: "",
      status: "Planlandı",
      customer_id: null,
      service_id: null,
      product_id: null,
      invoice_id: null,
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const eventData = {
          ...values,
          start_date: selectedEvent ? selectedEvent.start.toISOString() : dateRange[0].toISOString(),
          end_date: selectedEvent ? selectedEvent.end.toISOString() : dateRange[1].toISOString(),
          event_type: values.event_type.map(type => type.value),
          customer_id: values.customer_id ? [values.customer_id.value] : [],
          service_id: values.service_id ? [values.service_id.value] : [],
          product_id: values.product_id ? [values.product_id.value] : [],
          invoice_id: values.invoice_id ? [values.invoice_id.value] : [],
        };
        await pb.collection('events').create(eventData);
        resetForm();
        setDateRange([new Date(), new Date()]);
        toggle();
        onEventAdded();
      } catch (error) {
        console.error("Error saving event:", error);
      }
      setSubmitting(false);
    },
  });

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle} tag="h5" className="p-3 bg-info-subtle modal-title">
        Etkinlik Oluştur
      </ModalHeader>
      <ModalBody>
        <Form onSubmit={formik.handleSubmit}>
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label">Etkinlik Adı</Label>
                <Input
                  name="title"
                  type="text"
                  {...formik.getFieldProps('title')}
                  invalid={formik.touched.title && formik.errors.title}
                />
                {formik.touched.title && formik.errors.title && (
                  <FormFeedback type="invalid">{formik.errors.title}</FormFeedback>
                )}
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label">Etkinlik Tipi</Label>
                <Select
                  isMulti
                  name="event_type"
                  options={eventTypeOptions}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  value={formik.values.event_type}
                  onChange={(selectedOptions) => formik.setFieldValue('event_type', selectedOptions)}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <div className="mb-3">
                <Label>Etkinlik Tarihi</Label>
                <Flatpickr
                  className="form-control"
                  options={{
                    mode: "range",
                    dateFormat: "Y-m-d H:i",
                    enableTime: true,
                    time_24hr: true,
                  }}
                  value={dateRange}
                  onChange={(dates) => setDateRange(dates)}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label">Açıklama</Label>
                <Input
                  type="textarea"
                  rows="3"
                  {...formik.getFieldProps('description')}
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label">Konum</Label>
                <Input
                  name="location"
                  type="text"
                  {...formik.getFieldProps('location')}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label">Durum</Label>
                <Input
                  type="select"
                  name="status"
                  {...formik.getFieldProps('status')}
                >
                  <option value="Planlandı">Planlandı</option>
                  <option value="Tamamlandı">Tamamlandı</option>
                  <option value="İptal Edildi">İptal Edildi</option>
                </Input>
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label">Müşteri</Label>
                <Select
                  name="customer_id"
                  options={relatedData.customers}
                  className="basic-single"
                  classNamePrefix="select"
                  value={formik.values.customer_id}
                  onChange={(selectedOption) => formik.setFieldValue('customer_id', selectedOption)}
                  placeholder="Müşteri Seçiniz..."
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={3}>
              <div className="mb-3">
                <Label className="form-label">Hizmet</Label>
                <Select
                  name="service_id"
                  options={relatedData.services}
                  className="basic-single"
                  classNamePrefix="select"
                  value={formik.values.service_id}
                  onChange={(selectedOption) => formik.setFieldValue('service_id', selectedOption)}
                  placeholder="Hizmet Seçiniz..."
                />
              </div>
            </Col>
            <Col md={3}>
              <div className="mb-3">
                <Label className="form-label">Ürün</Label>
                <Select
                  name="product_id"
                  options={relatedData.products}
                  className="basic-single"
                  classNamePrefix="select"
                  value={formik.values.product_id}
                  onChange={(selectedOption) => formik.setFieldValue('product_id', selectedOption)}
                  placeholder="Ürün Seçiniz..."
                />
              </div>
            </Col>
            <Col md={3}>
              <div className="mb-3">
                <Label className="form-label">Teklif</Label>
                <Select
                  name="invoice_id"
                  options={relatedData.invoices}
                  className="basic-single"
                  classNamePrefix="select"
                  value={formik.values.invoice_id}
                  onChange={(selectedOption) => formik.setFieldValue('invoice_id', selectedOption)}
                  placeholder="Teklif Seçiniz..."
                />
              </div>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col xs={12}>
              <div className="d-flex gap-2 justify-content-end">
                <Button type="submit" color="success">
                  Etkinlik Oluştur
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default CreateEventModal;
